import React, { useMemo, useCallback } from 'react'
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  type Node,
  type Edge,
  type NodeProps,
  BackgroundVariant,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useTranslation } from 'react-i18next'
import MemberCard, { displayLabel } from './MemberCard'
import {
  RelationshipType,
  LineageType,
  type FamilyMember,
  type FamilyTreeOwner,
} from '../../services/family-tree.service'

interface FamilyTreeViewProps {
  owner: FamilyTreeOwner
  members: FamilyMember[]
  onEdit: (m: FamilyMember) => void
  onDelete: (m: FamilyMember) => void
}

const NODE_W = 200
const NODE_H = 116
const H_GAP = 32
const V_GAP = 56
const COL = NODE_W + H_GAP
const ROW = NODE_H + V_GAP

const isBioParentOfOwner = (m: FamilyMember) =>
  (m.relationship === RelationshipType.FATHER || m.relationship === RelationshipType.MOTHER) &&
  m.lineageType === LineageType.BIOLOGICAL

const dashed = (lt: LineageType) => lt === LineageType.ADOPTIVE

interface OwnerNodeData {
  owner: FamilyTreeOwner
  [key: string]: unknown
}
interface MemberNodeData {
  member: FamilyMember
  owner: FamilyTreeOwner
  allMembers: FamilyMember[]
  onEdit?: (m: FamilyMember) => void
  onDelete?: (m: FamilyMember) => void
  [key: string]: unknown
}

function buildLayout(
  owner: FamilyTreeOwner,
  members: FamilyMember[],
  callbacks: { onEdit: (m: FamilyMember) => void; onDelete: (m: FamilyMember) => void },
): { nodes: Node[]; edges: Edge[] } {
  const nodes: Node[] = []
  const edges: Edge[] = []

  const father = members.find((m) => m.relationship === RelationshipType.FATHER)
  const mother = members.find((m) => m.relationship === RelationshipType.MOTHER)
  const siblings = members.filter((m) => m.relationship === RelationshipType.SIBLING)
  const spouses = members.filter((m) => m.relationship === RelationshipType.SPOUSE)
  const children = members.filter((m) => m.relationship === RelationshipType.CHILD)

  // Owner anchored at x=0, y=ROW (generation 1)
  const ownerX = 0
  const ownerY = ROW
  nodes.push({
    id: '__owner',
    type: 'ownerNode',
    position: { x: ownerX - NODE_W / 2, y: ownerY },
    data: { owner } as OwnerNodeData,
  })

  const e = (
    from: string,
    to: string,
    opts: { dashed?: boolean; label?: string; animated?: boolean } = {},
  ) => {
    const id = `e-${from}-${to}`
    edges.push({
      id,
      source: from,
      target: to,
      type: 'smoothstep',
      style: {
        stroke: opts.dashed ? '#a855f7' : '#10b981',
        strokeWidth: 2,
        strokeDasharray: opts.dashed ? '6 4' : undefined,
      },
      label: opts.label,
      labelStyle: { fontSize: 11, fill: '#6b7280' },
      labelBgStyle: { fill: '#fff' },
    })
  }

  // Owner's parents (above, centered on owner)
  const ownerParents = [father, mother].filter(Boolean) as FamilyMember[]
  const opStart = ownerX - ((ownerParents.length - 1) * COL) / 2
  ownerParents.forEach((p, i) => {
    nodes.push({
      id: p.id,
      type: 'memberNode',
      position: { x: opStart + i * COL - NODE_W / 2, y: 0 },
      data: { member: p, owner, allMembers: members, ...callbacks } as MemberNodeData,
    })
    e(p.id, '__owner', { dashed: dashed(p.lineageType) })
  })

  // Siblings (left of owner, same row)
  siblings.forEach((s, i) => {
    const x = ownerX - (i + 1) * COL - NODE_W / 2
    nodes.push({
      id: s.id,
      type: 'memberNode',
      position: { x, y: ownerY },
      data: { member: s, owner, allMembers: members, ...callbacks } as MemberNodeData,
    })
    e(s.id, '__owner', { dashed: dashed(s.lineageType) })
  })

  // Children (below owner, centered on owner)
  const cStart = ownerX - ((children.length - 1) * COL) / 2
  children.forEach((c, i) => {
    nodes.push({
      id: c.id,
      type: 'memberNode',
      position: { x: cStart + i * COL - NODE_W / 2, y: ownerY + ROW },
      data: { member: c, owner, allMembers: members, ...callbacks } as MemberNodeData,
    })
    e('__owner', c.id, { dashed: dashed(c.lineageType) })
  })

  // Spouses (right of owner, each anchors its in-law sub-tree)
  spouses.forEach((sp, i) => {
    const spouseBaseX = ownerX + (i + 1) * COL
    nodes.push({
      id: sp.id,
      type: 'spouseNode',
      position: { x: spouseBaseX - NODE_W / 2, y: ownerY },
      data: { member: sp, owner, allMembers: members, ...callbacks } as MemberNodeData,
    })
    e('__owner', sp.id, { label: '♥' })

    // In-laws attached to this spouse, grouped by generation
    const inLaws = members.filter((m) => m.targetSpouseId === sp.id)
    const parentsInLaw = inLaws.filter(
      (m) =>
        m.relationship === RelationshipType.FATHER_IN_LAW ||
        m.relationship === RelationshipType.MOTHER_IN_LAW,
    )
    const grandInLaw = inLaws.filter(
      (m) => m.relationship === RelationshipType.GRANDPARENT_IN_LAW,
    )
    const extInLaw = inLaws.filter(
      (m) =>
        m.relationship === RelationshipType.AUNT_IN_LAW ||
        m.relationship === RelationshipType.UNCLE_IN_LAW ||
        m.relationship === RelationshipType.COUSIN_IN_LAW,
    )

    // Parents-in-law above the spouse (gen 0)
    const pStart = spouseBaseX - ((parentsInLaw.length - 1) * COL) / 2
    parentsInLaw.forEach((p, j) => {
      nodes.push({
        id: p.id,
        type: 'memberNode',
        position: { x: pStart + j * COL - NODE_W / 2, y: 0 },
        data: { member: p, owner, allMembers: members, ...callbacks } as MemberNodeData,
      })
      e(p.id, sp.id, { dashed: dashed(p.lineageType) })
    })

    // Grandparents-in-law above parents (gen -1)
    const gStart = spouseBaseX - ((grandInLaw.length - 1) * COL) / 2
    grandInLaw.forEach((g, j) => {
      nodes.push({
        id: g.id,
        type: 'memberNode',
        position: { x: gStart + j * COL - NODE_W / 2, y: -ROW },
        data: { member: g, owner, allMembers: members, ...callbacks } as MemberNodeData,
      })
      // Connect grandparent to the nearest parent-in-law of same spouse if any, else to spouse
      e(g.id, sp.id, {})
    })

    // Aunt/Uncle/Cousin-in-law: stack below spouse (gen 2)
    extInLaw.forEach((x, j) => {
      const colOffset = (j - (extInLaw.length - 1) / 2) * COL
      nodes.push({
        id: x.id,
        type: 'memberNode',
        position: { x: spouseBaseX + colOffset - NODE_W / 2, y: ownerY + ROW },
        data: { member: x, owner, allMembers: members, ...callbacks } as MemberNodeData,
      })
      e(sp.id, x.id, {})
    })
  })

  return { nodes, edges }
}

const OwnerNode = ({ data }: NodeProps) => {
  const { t } = useTranslation()
  const { owner } = data as unknown as OwnerNodeData
  const fullName = `${owner.firstName}${owner.lastName ? ' ' + owner.lastName : ''}`
  return (
    <div className="relative rounded-lg border-2 border-primary-600 bg-primary-50 p-2.5 shadow-md">
      <Handle type="target" position={Position.Top} style={{ background: '#10b981' }} />
      <Handle type="source" position={Position.Bottom} style={{ background: '#10b981' }} />
      <Handle type="source" position={Position.Right} id="r" style={{ background: '#10b981' }} />
      <Handle type="target" position={Position.Left} id="l" style={{ background: '#10b981' }} />
      <div className="flex items-center gap-1.5">
        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary-600 text-[10px] font-bold text-white">
          ★
        </span>
        <p className="truncate text-xs font-bold text-neutral-900">{fullName}</p>
      </div>
      <p className="mt-0.5 text-[11px] font-semibold text-primary-700">{t('familyTree.title')}</p>
    </div>
  )
}

const MemberNode = ({ data }: NodeProps) => {
  const { member, owner, allMembers, onEdit, onDelete } = data as unknown as MemberNodeData
  return (
    <div className="relative">
      <Handle type="target" position={Position.Top} style={{ background: '#10b981' }} />
      <Handle type="source" position={Position.Bottom} style={{ background: '#10b981' }} />
      <Handle type="target" position={Position.Left} id="l" style={{ background: '#10b981' }} />
      <Handle type="source" position={Position.Right} id="r" style={{ background: '#10b981' }} />
      <div style={{ width: NODE_W }}>
        <MemberCard member={member} owner={owner} allMembers={allMembers} onEdit={onEdit} onDelete={onDelete} variant="node" />
      </div>
    </div>
  )
}

const SpouseNode = ({ data }: NodeProps) => {
  const { member, owner, allMembers, onEdit, onDelete } = data as unknown as MemberNodeData
  return (
    <div className="relative">
      <Handle type="target" position={Position.Left} id="l" style={{ background: '#ec4899' }} />
      <Handle type="source" position={Position.Right} id="r" style={{ background: '#10b981' }} />
      <Handle type="target" position={Position.Top} id="t" style={{ background: '#10b981' }} />
      <Handle type="source" position={Position.Bottom} id="b" style={{ background: '#10b981' }} />
      <div style={{ width: NODE_W }}>
        <MemberCard member={member} owner={owner} allMembers={allMembers} onEdit={onEdit} onDelete={onDelete} variant="node" />
      </div>
    </div>
  )
}

const nodeTypes = {
  ownerNode: OwnerNode,
  memberNode: MemberNode,
  spouseNode: SpouseNode,
}

export default function FamilyTreeView({
  owner,
  members,
  onEdit,
  onDelete,
}: FamilyTreeViewProps) {
  const { t } = useTranslation()
  const { nodes, edges } = useMemo(
    () => buildLayout(owner, members, { onEdit, onDelete }),
    [owner, members, onEdit, onDelete],
  )

  const defaultEdgeOptions = useMemo(
    () => ({ type: 'smoothstep' }),
    [],
  )

  // Suppress unused warning for t (kept for future Panel usage)
  void t

  return (
    <div className="h-[70vh] w-full rounded-lg border border-neutral-200 bg-neutral-50">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        defaultEdgeOptions={defaultEdgeOptions}
        fitView
        fitViewOptions={{ padding: 0.15 }}
        minZoom={0.2}
        maxZoom={1.6}
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#d4d4d4" />
        <Controls showInteractive={false} />
        <MiniMap
          pannable
          zoomable
          nodeColor={(n) => (n.type === 'ownerNode' ? '#10b981' : '#fff')}
          nodeStrokeColor="#a3a3a3"
          maskColor="rgba(0,0,0,0.05)"
        />
      </ReactFlow>
    </div>
  )
}
